/**
 * Pure helpers for document lists and the generation flow: card snippets, presentation
 * detection, file names and per-field fill status of generated documents (CONTRACTS §5:
 * `data-field-id` + `data-field-status="filled|needs_input"`).
 */

const HEADER_FOOTER_RE = /<!--HEADER_FOOTER_START-->[\s\S]*?<!--HEADER_FOOTER_END-->\s*/g;
const NEEDS_INPUT_TEXT_RE = /\[Needs input:\s*([^\]]+)\]/i;
const FIELD_ELEMENT_RE = /<(span|div)\b([^>]*\bdata-field-id\s*=\s*("[^"]*"|'[^']*')[^>]*)>/gi;
const ATTR_STATUS_RE = /\bdata-field-status\s*=\s*("([^"]*)"|'([^']*)')/i;
const DEFAULT_SNIPPET_LENGTH = 200;

/**
 * Converts document HTML into plain text. Block boundaries become spaces first, so adjacent
 * blocks do not run together ("Solution DesignDocument Versions") once tags are stripped.
 * Header/footer metadata comments, HTML comments and <style>/<script> bodies are dropped.
 * @param {string} html - Document HTML; may be empty.
 * @returns {string} Plain text with whitespace collapsed.
 */
export function htmlToPreviewText(html) {
  return String(html || '')
    .replace(HEADER_FOOTER_RE, '')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<(style|script)\b[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<\/(?:p|h[1-6]|li|td|th|tr|div|blockquote|pre)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Short card snippet for a document.
 * @param {string} html - Document HTML.
 * @param {number} [max=200] - Max characters before an ellipsis.
 * @returns {string} Snippet ('' when the document has no text).
 */
export function buildDocumentSnippet(html, max = DEFAULT_SNIPPET_LENGTH) {
  const text = htmlToPreviewText(html);
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

/**
 * Whether a template type is a presentation.
 * @param {string} [templateType] - Template type.
 * @returns {boolean} True for PowerPoint templates.
 */
export function isPresentationTemplateType(templateType) {
  // template_type is the database enum {document, powerpoint}.
  return templateType === 'powerpoint';
}

/**
 * Whether a document is a presentation: by its template when known, else by the slide marker
 * the presentation generator embeds.
 * @param {{content?: string, templateId?: string}} doc - Document.
 * @param {Array<{id: string, templateType?: string}>} [templates] - Known templates.
 * @returns {boolean} True for decks.
 */
export function isPresentationDocument(doc, templates = []) {
  const template = (templates || []).find((t) => t.id === doc?.templateId);
  if (template) return isPresentationTemplateType(template.templateType);
  return /<!--ppt_slides:/.test(doc?.content || '');
}

/**
 * Base file name for exports: the document name without a known extension.
 * @param {string} name - Document name.
 * @returns {string} File name base.
 */
export function exportBaseName(name) {
  return String(name || 'document').replace(/\.(docx|pdf|html|pptx)$/i, '') || 'document';
}

/**
 * Read one attribute from a raw attribute string.
 * @param {string} attrs - Attribute source.
 * @param {RegExp} re - Attribute regex with value in group 2 or 3.
 * @returns {string|null} Value.
 */
function readAttr(attrs, re) {
  const match = re.exec(attrs);
  if (!match) return null;
  return match[2] !== undefined ? match[2] : match[3];
}

/**
 * Count generated fields by status from document HTML.
 * @param {string} html - Generated document HTML.
 * @returns {{total: number, filled: number, needsInput: number}} Counts (total 0 when the
 *   document has no field markup, e.g. generated by the pipeline that predates template fields).
 */
export function countFieldStatuses(html) {
  const counts = { total: 0, filled: 0, needsInput: 0 };
  const source = String(html || '');
  FIELD_ELEMENT_RE.lastIndex = 0;
  let match = FIELD_ELEMENT_RE.exec(source);
  const seen = new Set();
  while (match) {
    const attrs = match[2];
    const id = match[3].slice(1, -1);
    if (!seen.has(id)) {
      seen.add(id);
      const status = readAttr(attrs, ATTR_STATUS_RE);
      counts.total += 1;
      if (status === 'needs_input') counts.needsInput += 1;
      else counts.filled += 1;
    }
    match = FIELD_ELEMENT_RE.exec(source);
  }
  return counts;
}

/**
 * Distinct template variables ({{project.name}} etc.) the generator could not resolve, from the
 * chips it leaves in the HTML (`data-variable` + `data-field-status="needs_input"`).
 * @param {string} html - Generated document HTML.
 * @returns {string[]} Variable names in document order.
 */
export function listUnresolvedVariables(html) {
  const names = [];
  const re = /<span\b([^>]*\bdata-variable\s*=\s*("[^"]*"|'[^']*')[^>]*)>/gi;
  const source = String(html || '');
  let match = re.exec(source);
  while (match) {
    const status = readAttr(match[1], ATTR_STATUS_RE);
    const name = match[2].slice(1, -1);
    if (status === 'needs_input' && name && !names.includes(name)) names.push(name);
    match = re.exec(source);
  }
  return names;
}

/**
 * Non-negative integer from an unknown value.
 * @param {*} value - Value.
 * @returns {number} Integer >= 0.
 */
const toCount = (value) => {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Field fill summary for a generated document. Sections (AI fields) and unresolved template
 * variables are reported separately so the numbers always add up: `filled + needsInput === total`,
 * where `total` counts only fields that were filled or need input (fields the generator omitted
 * on purpose are not sections the user has to act on).
 * Sources, in order: the generation manifest's per-field statuses
 * (trust_metadata.manifest.perField), its aggregate counts (clamped, because the worker's
 * `needsInput` also includes unresolved variables), then the field markup in the HTML.
 * @param {{content?: string, trustMetadata?: object|null}} doc - Document.
 * @returns {{total: number, filled: number, needsInput: number, unresolvedVariables: string[]}|null}
 *   Summary, or null when the document carries no field information.
 */
export function summarizeFieldStatus(doc) {
  const meta = doc?.trustMetadata || null;
  const manifest = meta?.manifest || null;
  const unresolvedVariables = Array.isArray(manifest?.unresolvedVariables)
    ? manifest.unresolvedVariables.filter((name) => typeof name === 'string' && name)
    : listUnresolvedVariables(doc?.content);

  const perField = Array.isArray(manifest?.perField) ? manifest.perField : null;
  if (perField && perField.length > 0) {
    const filled = perField.filter((f) => f?.status === 'filled').length;
    const needsInput = perField.filter((f) => f?.status === 'needs_input').length;
    if (filled + needsInput > 0) {
      return { total: filled + needsInput, filled, needsInput, unresolvedVariables };
    }
  }

  const counts = manifest?.counts || meta?.counts;
  const total = counts ? toCount(counts.total) - toCount(counts.omitted) : 0;
  if (total > 0) {
    const filled = Math.min(toCount(counts.filled), total);
    const needsInput = Math.min(Math.max(0, toCount(counts.needsInput) - unresolvedVariables.length), total - filled);
    return { total: filled + needsInput, filled, needsInput, unresolvedVariables };
  }

  const fromHtml = countFieldStatuses(doc?.content);
  return fromHtml.total > 0 ? { ...fromHtml, unresolvedVariables } : null;
}

/**
 * Truncate a label for a chip.
 * @param {string} text - Label.
 * @param {number} max - Max characters.
 * @returns {string} Label.
 */
function clipLabel(text, max) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

/**
 * List the generated fields of a document with a readable label and status, in document order.
 * Labels come from (in order): the generation manifest's instruction for the field, the template's
 * field instruction, the "[Needs input: …]" text, then the nearest preceding heading.
 * Requires a DOM (DOMParser); returns [] without one.
 * @param {string} html - Generated document HTML.
 * @param {{perField?: Array<{id: string, baseId?: string, instruction?: string, status?: string}>, templateFields?: Array<{id: string, instruction: string}>, maxLabel?: number}} [options] - Label sources.
 * @returns {Array<{id: string, status: 'filled'|'needs_input', label: string, heading: string}>} Fields.
 */
export function listGeneratedFields(html, { perField = [], templateFields = [], maxLabel = 80 } = {}) {
  if (!html || typeof DOMParser === 'undefined') return [];
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const manifestById = new Map();
  (perField || []).forEach((entry) => {
    if (entry?.id) manifestById.set(entry.id, entry);
    if (entry?.baseId && !manifestById.has(entry.baseId)) manifestById.set(entry.baseId, entry);
  });
  const templateById = new Map((templateFields || []).map((f) => [f.id, f]));
  const fields = [];
  const seen = new Set();
  let heading = '';
  const walker = doc.createTreeWalker(doc.body, 1 /* NodeFilter.SHOW_ELEMENT */);
  let node = walker.nextNode();
  while (node) {
    const tag = node.tagName.toLowerCase();
    if (/^h[1-6]$/.test(tag)) {
      heading = (node.textContent || '').replace(/\s+/g, ' ').trim();
    } else if (node.hasAttribute('data-field-id')) {
      const id = node.getAttribute('data-field-id');
      if (id && !seen.has(id)) {
        seen.add(id);
        const status = node.getAttribute('data-field-status') === 'needs_input' ? 'needs_input' : 'filled';
        const manifest = manifestById.get(id);
        const templateField = templateById.get(id) || (manifest?.baseId ? templateById.get(manifest.baseId) : null);
        const needsInputText = NEEDS_INPUT_TEXT_RE.exec(node.textContent || '')?.[1];
        const label = manifest?.instruction || templateField?.instruction || needsInputText || heading || 'Untitled section';
        fields.push({ id, status, label: clipLabel(label, maxLabel), heading });
      }
    }
    node = walker.nextNode();
  }
  return fields;
}

/**
 * Next document name for a regenerated variant: "Name (v2)", "Name (v3)", ...
 * @param {string} baseName - Name of the first generation (without a variant suffix).
 * @param {number} variant - Variant number of the new generation (0 = first).
 * @returns {string} Document name.
 */
export function variantDocumentName(baseName, variant) {
  const clean = String(baseName || '').replace(/\s*\(v\d+\)$/, '').trim();
  return variant > 0 ? `${clean} (v${variant + 1})` : clean;
}

/**
 * Generation warnings of a deck, from the trust_metadata the presentation worker writes
 * (kind 'presentation': manifest.counts, presentation.fallbackSlots, warnings). Decks generated
 * before this metadata existed return null, as do decks with nothing to warn about.
 * @param {{trustMetadata?: object|null, trust_metadata?: object|null}} doc - Document (local or row shape).
 * @returns {{total: number, fallbackSlots: number, aiFailed: boolean, warnings: string[]}|null} Summary.
 */
export function summarizeDeckGeneration(doc) {
  const meta = doc?.trustMetadata || doc?.trust_metadata || null;
  if (!meta || meta.kind !== 'presentation') return null;
  const counts = meta.manifest?.counts || {};
  const fallbackSlots = toCount(meta.presentation?.fallbackSlots ?? counts.needsInput);
  const total = Math.max(toCount(counts.total), fallbackSlots);
  const aiFailed = Boolean(meta.presentation?.aiError);
  const warnings = Array.isArray(meta.warnings)
    ? meta.warnings.filter((w) => typeof w === 'string' && w.trim()).map((w) => w.trim())
    : [];
  if (fallbackSlots === 0 && !aiFailed) return null;
  return { total, fallbackSlots, aiFailed, warnings };
}

/**
 * Slide slots of a generated deck with their status, from trust_metadata.manifest.perField
 * (decks have no field markup in their HTML). Labels are the slot's prompt; `heading` names the
 * slides the slot appears on.
 * @param {object|null} trustMetadata - Deck trust_metadata (kind 'presentation').
 * @param {{maxLabel?: number}} [options] - Label length.
 * @returns {Array<{id: string, status: 'filled'|'needs_input', label: string, heading: string}>} Slots.
 */
export function listDeckSlots(trustMetadata, { maxLabel = 80 } = {}) {
  if (trustMetadata?.kind !== 'presentation' || !Array.isArray(trustMetadata?.manifest?.perField)) return [];
  return trustMetadata.manifest.perField
    .filter((slot) => slot && typeof slot.id === 'string' && slot.id)
    .map((slot) => {
      const slides = Array.isArray(slot.slides) ? slot.slides.filter((n) => Number.isInteger(n) && n > 0) : [];
      return {
        id: slot.id,
        status: slot.status === 'needs_input' ? 'needs_input' : 'filled',
        label: clipLabel(slot.instruction || slot.name || slot.id, maxLabel),
        heading: slides.length > 0 ? `Slide${slides.length === 1 ? '' : 's'} ${slides.join(', ')}` : '',
      };
    });
}
